import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';

import IntegrationHeroCard from 'dashboard/components/IntegrationHeroCard';
import OauthPopup from 'dashboard/components/OauthPopup';
import { Container, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { CreateExternalIntegrationParams } from 'dashboard/redux/thunk-actions/external-integrations';
import ExternalIntegrationHeader from 'dashboard/views/DashboardExternalIntegrationsShowView/ExternalIntegrationHeader';
import { ProductFeatureContactUsBanner } from 'dashboard/views/DashboardExternalIntegrationsShowView/ProductFeatureContactUsBanner';
import ExternalIntegrationOAuthDetailsTable from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/ExternalIntegrationOAuthDetailsTable';
import ExternalIntegrationOAuthErrorModal from 'dashboard/views/DashboardExternalIntegrationsShowView/Productivity/ExternalIntegrationOAuthErrorModal';
import Button from 'design-system/components/Button';
import SlackIcon from 'images/icons/marketplace/SlackIcon.svg';
import {
  LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL,
  LEGACY_EXTERNAL_INTEGRATION_VENDORS,
  LegacyVendorName,
} from 'lib/constants/externalIntegrationVendors';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

export interface SlackConfigurationProps {
  editable: boolean;
  isLoading: boolean;
  externalIntegrations: ExternalIntegration[];
  createExternalIntegration: (
    externalIntegrationType: ExternalIntegration['type'],
    integrationParams: CreateExternalIntegrationParams,
  ) => void;
  setSelectedIntegration: (integration: ExternalIntegration) => void;
}

const OAUTH_SCOPE =
  'channels:join,channels:manage,channels:read,chat:write,groups:read,groups:write,im:read,im:write,mpim:read,mpim:write,users:read';
const OAUTH_CLIENT_ID = '397854381168.4657689230771';

const SlackConfiguration = ({
  editable,
  isLoading,
  externalIntegrations,
  createExternalIntegration,
  setSelectedIntegration,
}: SlackConfigurationProps) => {
  const [showErrorModal, { enable: openErrorModal, disable: closeErrorModal }] = useToggle(false);
  const onCode = (code: string) => {
    createExternalIntegration(ApiModel.ExternalIntegrationProductivitySlackOAuth, {
      code: code,
      'redirect-url': LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL,
    });
  };

  const authorizationUrl = `https://slack.com/oauth/v2/authorize?client_id=${OAUTH_CLIENT_ID}&redirect_uri=${LEGACY_EXTERNAL_INTEGRATION_OAUTH_REDIRECT_URL}&scope=${OAUTH_SCOPE}`;
  const slackVendor = LEGACY_EXTERNAL_INTEGRATION_VENDORS.find(
    (vendor) => vendor.name === LegacyVendorName.Slack,
  );

  return (
    <Container>
      <ExternalIntegrationOAuthErrorModal isOpen={showErrorModal} handleClose={closeErrorModal} />
      <ExternalIntegrationHeader editable={editable}>
        <OauthPopup
          url={authorizationUrl}
          onCode={onCode}
          onError={openErrorModal}
          disabled={!editable}
        >
          <Button
            id='slack__add-connection-button'
            width='auto'
            aria-label='add button'
            disabled={!editable}
            prefixIcon={<IconPlus />}
          >
            Add Connection
          </Button>
        </OauthPopup>
      </ExternalIntegrationHeader>
      <Section>
        <ProductFeatureContactUsBanner />
        <IntegrationHeroCard title='Slack' image={SlackIcon}>
          {slackVendor != null && slackVendor.description}
        </IntegrationHeroCard>
        <ExternalIntegrationOAuthDetailsTable
          isLoading={isLoading}
          editable={editable}
          displayName='Slack'
          connectionName='Workspace'
          externalIntegrations={externalIntegrations}
          setSelectedIntegration={setSelectedIntegration}
        />
      </Section>
    </Container>
  );
};

export default SlackConfiguration;
